<template>
  <article
    class="sw-appt"
    :class="{ 'has-marker': markerColor, 'is-reception': isReception }"
    :style="cardStyle"
    @click.stop="$emit('click', schedule)"
  >
    <button
      v-if="isReception"
      type="button"
      class="sw-appt__photo"
      title="Foto do paciente"
      @click.stop="$emit('takePicture', schedule)"
    >
      <img v-if="photoUrl" :src="photoUrl" alt="">
      <span v-else>{{ initials }}</span>
    </button>

    <div class="sw-appt__body">
      <div class="sw-appt__title">
        <div class="sw-appt__name">
          <span class="sw-appt__kind" :class="{ 'is-exam': isExam }" :title="isExam ? 'Exame' : 'Consulta'">{{ isExam ? 'E' : 'C' }}</span>
          <strong>{{ schedule.patient_name }}</strong>
          <button
            type="button"
            class="sw-appt__copy"
            title="Copiar nome"
            @click.stop="copyName"
          >
            <i class="bi bi-clipboard-fill"></i>
          </button>
        </div>
        <span v-if="!isReception" class="sw-appt__status" :style="statusStyle">{{ statusLabel }}</span>
      </div>
      <PatientTagBadges :tags="schedule.tags" />

      <template v-if="!isReception">
        <p v-if="hasPrimaryLine" class="sw-appt__line">
          <span v-if="schedule.spec_name">{{ schedule.spec_name }}</span>
          <span v-if="schedule.paymeth_name">{{ schedule.paymeth_name }}</span>
          <span v-if="schedule.sched_online">online</span>
          <ScheduleRecurrenceBadge
            :session="schedule.sched_recur_session"
            :total="schedule.sched_recur_total"
            @open="$emit('openRecurrence', schedule)"
          />
        </p>
        <p v-if="schedule.prof_name" class="sw-appt__line" :title="schedule.prof_name">{{ schedule.prof_name }}</p>
        <p
          v-if="schedule.insurance_name"
          class="sw-appt__line sw-appt__insurance"
          :title="schedule.insurance_name"
        >{{ schedule.insurance_name }}</p>
        <p v-if="phone" class="sw-appt__line">{{ phone }}</p>
      </template>

      <p v-else-if="hasMeta" class="sw-appt__meta">
        <span v-if="schedule.spec_name">{{ schedule.spec_name }}</span>
        <span v-if="schedule.paymeth_name">{{ schedule.paymeth_name }}</span>
        <span
          v-if="schedule.insurance_name"
          class="sw-appt__insurance"
          :title="schedule.insurance_name"
        >{{ schedule.insurance_name }}</span>
        <span v-if="phone">{{ phone }}</span>
        <span v-if="schedule.sched_online">online</span>
        <span v-if="showProf && schedule.prof_name">{{ schedule.prof_name }}</span>
        <ScheduleRecurrenceBadge
          :session="schedule.sched_recur_session"
          :total="schedule.sched_recur_total"
          @open="$emit('openRecurrence', schedule)"
        />
      </p>

      <div v-if="clockMarks.length" class="sw-appt__clocks">
        <span
          v-for="mark in clockMarks"
          :key="mark.key"
          class="sw-appt__clock"
          :class="'is-' + mark.tone"
          :title="mark.label"
        >{{ mark.time }}</span>
      </div>
      <span v-if="isReception" class="sw-appt__status" :style="statusStyle">{{ statusLabel }}</span>
      <div class="sw-appt__actions" @click.stop>
        <BtnCallQueueArrival :scheduleEdit="schedule" :classes="callClasses" />
        <BtnArriveSchedule
          :scheduleEdit="schedule"
          :classes="arriveClasses"
          :btnText="arriveText"
          @onArriveSchedule="$emit('onArriveSchedule', $event)"
        />
        <BtnFinishOrReOpenSchedule
          :scheduleEdit="schedule"
          :classes="finishClasses"
          :btnText="finishText"
          @onFinishSchedule="$emit('onFinishSchedule', $event)"
          @onReopenSchedule="$emit('onReopenSchedule', $event)"
        />
        <BtnStartAttendanceSchedule
          :scheduleEdit="schedule"
          :classes="startClasses"
          :btnText="startText"
          @onStartAttendance="$emit('onStartAttendance', $event)"
        />
      </div>
    </div>
  </article>
</template>

<script>
import strMask from '../../../helpers/strMask';
import str from '../../../helpers/str';
import Alert from '../../../helpers/alert';
import BtnArriveSchedule from '../BtnArriveSchedule.vue';
import BtnCallQueueArrival from '../BtnCallQueueArrival.vue';
import BtnFinishOrReOpenSchedule from '../BtnFinishOrReOpenSchedule.vue';
import BtnStartAttendanceSchedule from '../BtnStartAttendanceSchedule.vue';
import PatientTagBadges from '../PatientTagBadges.vue';
import ScheduleRecurrenceBadge from '../ScheduleRecurrenceBadge.vue';

const STATUS_COLOR = {
  AG: '#009ef7',
  CO: '#50cd89',
  FI: '#a1a5b7',
  CM: '#f1416c',
};

export default {
  name: 'ScheduleAppointmentCard',
  components: {
    BtnArriveSchedule,
    BtnCallQueueArrival,
    BtnFinishOrReOpenSchedule,
    BtnStartAttendanceSchedule,
    PatientTagBadges,
    ScheduleRecurrenceBadge,
  },
  props: {
    schedule: { type: Object, required: true },
    showTime: { type: Boolean, default: false },
    showProf: { type: Boolean, default: false },
    showSpec: { type: Boolean, default: false },
    variant: { type: String, default: 'agenda' },
  },
  emits: [
    'click',
    'takePicture',
    'openRecurrence',
    'onArriveSchedule',
    'onFinishSchedule',
    'onReopenSchedule',
    'onStartAttendance',
  ],
  computed: {
    isReception() {
      return this.variant === 'reception';
    },
    photoUrl() {
      return this.schedule.patient_photo ? str.url(this.schedule.patient_photo) : '';
    },
    initials() {
      return str.initialsName(this.schedule.patient_name).toUpperCase();
    },
    isExam() {
      return Boolean(this.schedule.kind_exam);
    },
    phone() {
      if (this.schedule.patient_phone) {
        return strMask.it(this.schedule.patient_phone, '(##) #####-####');
      }
      return '';
    },
    hasPrimaryLine() {
      return Boolean(
        this.schedule.spec_name
        || this.schedule.paymeth_name
        || this.schedule.sched_online
        || (this.schedule.sched_recur_session != null && Number(this.schedule.sched_recur_total) > 0)
      );
    },
    hasMeta() {
      return Boolean(
        this.schedule.spec_name
        || this.schedule.paymeth_name
        || this.schedule.insurance_name
        || this.phone
        || this.schedule.sched_online
        || (this.showProf && this.schedule.prof_name)
        || (this.schedule.sched_recur_session != null && Number(this.schedule.sched_recur_total) > 0)
      );
    },
    clockMarks() {
      const marks = [];
      if (this.schedule.sched_time) {
        marks.push({ key: 'sched', label: 'Hora de agendamento', time: this.formatClock(this.schedule.sched_time), tone: 'sched' });
      }
      if (this.schedule.called_at) {
        marks.push({ key: 'call', label: 'Hora da chamada', time: this.formatClock(this.schedule.called_at), tone: 'call' });
      }
      if (this.schedule.sched_arrive) {
        marks.push({ key: 'arrive', label: 'Hora da chegada', time: this.formatClock(this.schedule.sched_arrive), tone: 'arrive' });
      }
      if (this.schedule.sched_start) {
        marks.push({ key: 'start', label: 'Hora de início do atendimento', time: this.formatClock(this.schedule.sched_start), tone: 'start' });
      }
      if (this.schedule.sched_end) {
        marks.push({ key: 'end', label: 'Hora de término', time: this.formatClock(this.schedule.sched_end), tone: 'end' });
      }
      return marks.filter((mark) => mark.time);
    },
    canceled() {
      return Boolean(this.schedule.is_sched_canceled || this.schedule.sched_status === 'CM');
    },
    inCare() {
      if (this.canceled) return false;
      return Boolean(this.schedule.sched_start && this.schedule.sched_status !== 'FI');
    },
    waiting() {
      if (this.canceled) return false;
      return Boolean(this.schedule.sched_arrive && !this.schedule.sched_start && this.schedule.sched_status !== 'FI');
    },
    statusLabel() {
      if (this.schedule.sched_status === 'CM') return 'Cancelado pelo profissional';
      if (this.canceled) return 'Cancelado';
      if (this.schedule.sched_status === 'FI') return 'Finalizado';
      if (this.inCare) return 'Em atendimento';
      if (this.waiting) return 'Aguardando';
      if (this.schedule.sched_status === 'CO') return 'Confirmada';
      return 'Agendada';
    },
    markerColor() {
      const color = String(this.schedule.ps_color || '').trim();
      if (!color) return '';
      if (['#000', '#000000', 'black', '#00000000'].includes(color.toLowerCase())) return '';
      return color;
    },
    statusColor() {
      if (this.schedule.sched_status === 'CM') return '#2c0b0e';
      if (this.canceled) return STATUS_COLOR.CM;
      if (this.inCare) return '#7239ea';
      if (this.waiting) return '#ffc700';
      return STATUS_COLOR[this.schedule.sched_status] || '#780f5a';
    },
    statusStyle() {
      return {
        background: this.statusColor,
        color: this.waiting ? '#181c32' : '#fff',
      };
    },
    cardStyle() {
      const style = { borderLeftColor: this.statusColor };
      if (this.markerColor) style.borderBottomColor = this.markerColor;
      return style;
    },
    callClasses() {
      return this.isReception ? 'btn-icon btn-sm' : 'btn-sm';
    },
    arriveClasses() {
      if (!this.isReception) return 'btn-sm';
      const icon = this.schedule.sched_arrive ? 'bi-calendar-x' : 'bi-calendar-check';
      return `btn-icon btn-sm bi ${icon}`;
    },
    arriveText() {
      if (this.isReception) return '';
      return this.schedule.sched_arrive ? 'Estornar' : 'Chegou';
    },
    finishClasses() {
      const finished = this.schedule.sched_status === 'FI' || this.schedule.is_sched_finished;
      const color = finished ? 'btn-light-info' : 'btn-info';
      if (!this.isReception) return `btn-sm ${color}`;
      const icon = finished ? 'bi-arrow-counterclockwise' : 'bi-check2-square';
      return `btn-icon btn-sm bi ${icon} ${color}`;
    },
    finishText() {
      if (this.isReception) return '';
      return this.schedule.sched_status === 'FI' ? 'Reabrir' : 'Finalizar';
    },
    startClasses() {
      if (!this.isReception) return 'btn-sm';
      const icon = this.schedule.sched_start ? 'bi-arrow-clockwise' : 'bi-play-fill';
      return `btn-icon btn-sm bi ${icon}`;
    },
    startText() {
      if (this.isReception) return '';
      return this.schedule.sched_start ? 'Continuar' : 'Atender';
    },
  },
  methods: {
    date: moment,
    formatClock(value) {
      if (!value) return '';
      const raw = String(value);
      if (/^\d{2}:\d{2}/.test(raw)) return raw.slice(0, 5);
      const parsed = this.date(value);
      return parsed && parsed.isValid() ? parsed.format('HH:mm') : '';
    },
    copyName() {
      const name = this.schedule.patient_name;
      if (!name) return;
      navigator.clipboard.writeText(name).then(() => {
        Alert.toastSuccess('Nome copiado para a área de transferência!');
      }).catch(() => {
        Alert.toastError('Erro ao copiar o nome.');
      });
    },
  },
};
</script>

<style scoped>
.sw-appt {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0.4rem 0.6rem;
  border: 1px solid #e4e6ef;
  border-left: 4px solid #780f5a;
  border-radius: 0.6rem;
  background: #fff;
  cursor: pointer;
  min-width: 0;
}
.sw-appt.is-reception {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr);
  gap: 0.55rem;
  align-items: start;
  padding: 0.45rem 0.6rem;
}
.sw-appt.has-marker {
  border-bottom-width: 3px;
  border-bottom-style: solid;
}
.sw-appt:hover {
  background: #fbfafc;
}
.sw-appt__photo {
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  overflow: hidden;
  background: #f8f5f7;
  color: #780f5a;
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 40px;
  text-align: center;
}
.sw-appt__photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.sw-appt__body,
.sw-appt__title {
  min-width: 0;
}
.sw-appt__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
}
.sw-appt :deep(.patient-tag-badges) {
  margin-top: 0.16rem;
}
.sw-appt :deep(.patient-tag-badges .badge) {
  padding: 0.1rem 0.35rem;
  font-size: 0.62rem;
}
.sw-appt__name {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 0.28rem;
}
.sw-appt__kind {
  flex-shrink: 0;
  width: 1.2rem;
  height: 1.2rem;
  border-radius: 0.28rem;
  background: #f1faff;
  color: #009ef7;
  font-size: 0.65rem;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.sw-appt__kind.is-exam {
  background: #fff8dd;
  color: #c27803;
}
.sw-appt__title strong {
  min-width: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #181c32;
  line-height: 1.25;
}
.sw-appt:not(.is-reception) .sw-appt__title strong {
  white-space: normal;
  overflow: visible;
}
.sw-appt.is-reception .sw-appt__title {
  align-items: flex-start;
}
.sw-appt.is-reception .sw-appt__name {
  flex: 1 1 auto;
  min-width: 0;
  align-items: flex-start;
}
.sw-appt.is-reception .sw-appt__title strong {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  line-height: 1.25;
}
.sw-appt__copy {
  flex-shrink: 0;
  width: 1.35rem;
  height: 1.35rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: #780f5a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.sw-appt__copy i {
  font-size: 0.85rem;
}
.sw-appt__status {
  flex-shrink: 0;
  padding: 0.16rem 0.48rem;
  border-radius: 999px;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.sw-appt.is-reception .sw-appt__status {
  display: inline-flex;
  width: fit-content;
  max-width: 100%;
  margin-top: 0.35rem;
}
.sw-appt__clocks {
  display: flex;
  flex-wrap: wrap;
  gap: 0.28rem;
  margin-top: 0.28rem;
}
.sw-appt__clock {
  display: inline-flex;
  align-items: center;
  padding: 0.16rem 0.42rem;
  border-radius: 0.35rem;
  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: 0.01em;
}
.sw-appt__clock.is-sched {
  background: #009ef7;
  color: #fff;
}
.sw-appt__clock.is-call {
  background: #e8fff3;
  color: #50cd89;
}
.sw-appt__clock.is-arrive {
  background: #50cd89;
  color: #fff;
}
.sw-appt__clock.is-start {
  background: #ffc700;
  color: #181c32;
}
.sw-appt__clock.is-end {
  background: #7239ea;
  color: #fff;
}
.sw-appt__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.15rem 0;
  margin: 0.12rem 0 0;
  min-width: 0;
  color: #7e8299;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.3;
}
.sw-appt__line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin: 0.04rem 0 0;
  min-width: 0;
  color: #7e8299;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.3;
}
.sw-appt__insurance {
  display: block;
  min-width: 0;
  line-height: 1.25;
  white-space: normal;
  overflow: visible;
}
.sw-appt__meta > span + span::before,
.sw-appt__meta > span + :deep(.badge)::before,
.sw-appt__line > span + span::before,
.sw-appt__line > span + :deep(.badge)::before {
  content: '·';
  margin: 0 0.28rem;
  color: #c4cada;
  font-weight: 700;
}
.sw-appt__meta :deep(.badge),
.sw-appt__line :deep(.badge) {
  padding: 0.1rem 0.35rem;
  font-size: 0.62rem;
}
.sw-appt__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.28rem;
  margin-top: 0.3rem;
}
.sw-appt__actions :deep(.btn) {
  margin: 0 !important;
  flex: 0 0 auto;
  width: auto;
  min-height: 1.85rem !important;
  padding: 0.2rem 0.6rem !important;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.15;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.sw-appt.is-reception .sw-appt__actions :deep(.btn) {
  width: 2rem;
  height: 2rem;
  min-width: 2rem;
  min-height: 2rem;
  padding: 0;
}
.sw-appt__actions :deep(.btn.bi::before) {
  margin-right: 0;
  font-size: 0.78rem;
}
</style>
